//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { ColumnModel } from '../api/api.models'
import { Api, LocalDictionaryFactory } from './../api/api.module'

import { OperatorService } from '../../services/operator'
import { SystemService } from '../../services/system'
import { UserSessionService } from '../../services/user-session'
import { GridContextMenuService } from '../../services/grid'

import { CommonHelper } from '../../helpers/common-helper'
import { EventsNames, TemplateHelper, ConfigHelper, LocalizationHelper } from '../../helpers'

import { Activity, Enums } from '../../models/common'
import { OpOperator } from '../../models/core'

import { ImportOptions } from '../common/import'
import { ExportOptions } from '../common/export'
import { IMRConfirmationPopupOptions, IIMRConfirmationPopup, IMRConfirmationPopupInitializedArgs } from '../common/confirmation-popup2'
import { BaseGridController, ScopeBaseGrid } from '../common/common.base.grid.controller'

import { GridTypes, GridController, GridButtonsOptions, GridCustomButtonsOptions, GridCustomButtonLocation, GridCustomButtonOptions } from '../common/grid'


export class OperatorsController extends BaseGridController {

    //#region Consts

    private static get OPERATOR_GRID_NAME(): string { return 'UsersGrid' }

    //#endregion


    //#region Members

    public confirmationPopupOptions: IMRConfirmationPopupOptions

    private confirmationPopup: IIMRConfirmationPopup
    private copyGridButton: DevExpress.ui.dxButton
    private grid: GridController<OpOperator>

    //#endregion


    //#region Ctor

    constructor(
        protected $scope: ScopeBaseGrid,
        protected $state: StateService,
        $timeout: ng.ITimeoutService,
        protected api: Api,
        protected localDictionary: LocalDictionaryFactory,
        private gridContextMenuService: GridContextMenuService,
        protected userSessionService: UserSessionService,
        private operatorService: OperatorService,
        protected systemService: SystemService,
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSessionService, systemService, api, localDictionary, null, referenceType)

        this.initializeConfirmationPopup()
        this.setEvents()
        this.setMainGridOptions()
    }

    //#endregion


    //#region Events

    private onAdd(): void {
        this.systemService.openCreateEdit(this.operatorService.getCreateEditOptions())
    }

    private onConfirmDelete(): void {
        this.operatorService
            .deleteOperator(this.grid.selectedItem)
            .then(() => {
                this.onDeleted()
            })
    }

    private onCopy(): void {
        if (this.grid.selectedCount === 1) {
            this.systemService.openCreateEdit(this.operatorService.getCreateEditOptions(this.grid.selectedItemId, null, true))
        }
    }

    private onDelete(): void {
        this.confirmationPopup.show()
    }

    private onDeleted(): void {
        this.grid.dxGrid.refresh()
    }

    private onEdit(): void {
        if (this.grid.selectedCount === 1) {
            this.systemService.openCreateEdit(this.operatorService.getCreateEditOptions(this.grid.selectedItemId))
        }
        else {
            const columnCount = this.grid.dxGrid.columnCount()
            const columns = []

            for (let i = 0; i < columnCount; i++) {
                columns.push(this.grid.dxGrid.columnOption(i));
            }

            this.systemService.openBulkCreateEdit(this.operatorService.getBulkCreateEditOptions(angular.copy(this.grid.selectedGridItems), columns))
        }
    }

    private onInitialized(e: any, grid: GridController<OpOperator>): void {
        this.dataGrid = e.component
        this.grid = grid

        this.setGridId(e.tableId)
    }

    private onShowDetails(e: any): void {
        this.systemService.openDetails(this.operatorService.getDetailsOptions(this.grid.selectedItemId, this.grid.pageItemsIds))
    }

    private onSelectionChanged(): void {
        this.toggleCopyButton()
    }

    //#endregion


    //#region Confirmation Popup

    private initializeConfirmationPopup(): void {
        this.confirmationPopupOptions = {
            content: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => {
                this.onConfirmDelete()
            },

            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            }
        }
    }

    //#endregion


    //#region Copy

    private toggleCopyButton(): void {
        if (this.copyGridButton != null && this.grid != null) {
            this.copyGridButton.option('disabled', this.grid.selectedCount !== 1)
        }
    }

    //#endregion


    //#region Setup

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items = this.gridContextMenuService.getContextMenuItemsByReferenceType(Enums.ReferenceType.IdOperator, {
            idReferenceTypes: this.grid.selectedItemsIds,
            onEdit: () => this.onEdit(),
            onCopy: () => this.onCopy()
        })

        return this.mergeWithDefaultItems(isTouchScreen, items)
    }

    public getExportOptions(): ExportOptions {
        return {
            referenceType: this.referenceType,
            pluginOptions: {
                moduleName: this.moduleName,
                popupName: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__EXPORT, this.moduleName),
            },
        }
    }

    private getGridButtonsOptions(): GridButtonsOptions {
        return {
            enabled: true,

            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_ADD) ? "['" + Activity[Activity.OPERATOR_ADD] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_DEL) ? "['" + Activity[Activity.OPERATOR_DEL] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_EDIT) ? "['" + Activity[Activity.OPERATOR_EDIT] + "']" : null,
            exportPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_EXPORT_OP) ? "['" + Activity[Activity.OPERATOR_EXPORT_OP] + "']" : null,
            importPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_IMPORT_OP) ? "['" + Activity[Activity.OPERATOR_IMPORT_OP] + "']" : null,
            saveColumnSettingsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,
            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_LIST) ? "['" + Activity[Activity.OPERATOR_LIST] + "']" : null,

            onAddClick: () => this.onAdd(),
            onEditClick: () => this.onEdit(),
            onDeleteClick: () => this.onDelete(),
            onShowClick: (e: any) => this.onShowDetails(e),
        }
    }

    private getGridCustomButtonOptions(): GridCustomButtonsOptions {
        const customButtons: GridCustomButtonOptions[] = []

        if (this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_COPY)) {
            customButtons.push({
                location: GridCustomButtonLocation.Dropdown,
                options: {
                    template: TemplateHelper.getIcon(ConfigHelper.Icon.CONTENT_COPY, this.userSession),
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Copy),

                    onClick: (e: any) => {
                        this.onCopy()
                    },

                    onInitialized: (e: any) => {
                        this.copyGridButton = e.component

                        this.toggleCopyButton()
                    }
                }
            })
        }

        return {
            buttons: customButtons
        }
    }

    private getImportOptions(): ImportOptions {
        return {
            columnsReorderingDisabled: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OperatorModuleMainGrid),
            dictionaryMethodName: null,
            moduleName: this.moduleName,
            popupName: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__IMPORT, this.moduleName),
            referenceType: this.referenceType,
            requestMethodType: null,
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onDeleted()
        )

        this.$scope.showDetails = (e: any) => this.onShowDetails(e)
    }

    private setMainGridOptions(): void {
        this.operatorService
            .getMainGridColumns()
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.$scope.mainGridOptions = {
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    columnsDictionary: columns,
                    customButtonOptions: this.getGridCustomButtonOptions(),
                    defaultColumnsDictionary: defaultColumns,
                    idReferenceType: this.referenceType,
                    importOptions: this.getImportOptions(),
                    exportOptions: this.getExportOptions(),
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.userSaved,
                    name: OperatorsController.OPERATOR_GRID_NAME,
                    preExecuteClearFilter: this.preExecuteGridClearFilter,
                    referenceId: this.referenceType,
                    type: GridTypes.MAIN,

                    allowColumnReordering: !this.userSessionService
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OperatorModuleMainGrid),

                    getDataSourceMethod: this.userSessionService.getDataProvider().Operator.getOperatorFilter,

                    contextMenuItems: (isTouchScreen: boolean = false) => this.getContextMenuItems(isTouchScreen),
                    onInitialized: (e: any, grid: GridController<OpOperator>) => this.onInitialized(e, grid),
                    onDataReady: (data: any) => this.onDataReady(data),
                    onRowDoubleClick: (e: any) => this.onShowDetails(e),
                    onSelectionChanged: (e: any) => this.onSelectionChanged()
                }
            })
    }

    //#endregion
}
